import { Link } from 'react-router-dom'
import SEO from '../components/SEO'

const values = [
  {
    title: 'Ship & Iterate',
    description: 'We move swiftly, refining our approach with every step to maintain a leading edge.',
  },
  {
    title: 'Trusted Pair of Hands',
    description: 'Dependable and steadfast, we are always there when it matters most.',
  },
  {
    title: 'Overdeliver on the Promise',
    description: "Exceeding expectations is our standard, going beyond what's assured.",
  },
  {
    title: 'Clear is Kind',
    description: 'Transparent, honest communication keeps everyone on the same page.',
  },
]

const stats = [
  { value: '5+', label: 'Years', detail: 'Decade-class thinking with startup speed.' },
  { value: '2000+', label: 'Clients', detail: 'Businesses supported across sectors.' },
  { value: '20+', label: 'Awards', detail: 'Recognized for quality and innovation.' },
  { value: '99%', label: 'Success', detail: 'Reliable outcomes through execution.' },
]

const About = () => {
  return (
    <div>
      <SEO title="About Us" description="Learn about MindVista's journey, our mission, and the team driving digital transformation." />
      <section className="relative min-h-[70vh] overflow-hidden">
        <video autoPlay muted loop playsInline className="absolute inset-0 h-full w-full object-cover">
          <source src="/images/about/5120996_Woman_Man_3840x2160.mp4" type="video/mp4" />
        </video>
        <div className="absolute inset-0 bg-gradient-to-b from-black/80 via-black/65 to-black/85" />

        <div className="container-main relative z-10 flex min-h-[70vh] flex-col items-center justify-center text-center">
          <span className="section-kicker">About Us</span>
          <h1 className="mt-5 max-w-5xl text-4xl font-bold md:text-6xl">Empowering Businesses, Inspiring Innovation</h1>
          <p className="mt-5 max-w-4xl text-lg text-[#b0b0b0] md:text-xl">
            At MindVista, we specialize in transforming businesses with enterprise-grade software solutions tailored to their needs. With a legacy of technical excellence and a passion for innovation, we help organizations thrive in an ever-evolving digital landscape.
          </p>
        </div>
      </section>

      <section className="py-20">
        <div className="container-main grid gap-6 lg:grid-cols-2">
          <article className="neo-card p-8 md:p-10">
            <span className="section-kicker">Our Philosophy</span>
            <h2 className="mt-4 text-3xl font-semibold md:text-5xl">Empowering people through innovation</h2>
            <p className="mt-4 text-base leading-relaxed text-[#a3a3a3] md:text-lg">
              Our philosophy is simple: empower people and businesses through innovation. We foster collaboration, invest in talent, and deliver meaningful solutions that drive progress for our clients and communities worldwide.
            </p>
          </article>

          <article className="neo-card p-8 md:p-10">
            <h3 className="text-2xl font-semibold text-[rgb(249,193,37)] md:text-3xl">Our Mission</h3>
            <p className="mt-3 text-[#a8a8a8]">Building intelligent digital products that accelerate business growth and improve user outcomes.</p>
            <h3 className="mt-7 text-2xl font-semibold text-[rgb(249,193,37)] md:text-3xl">Our Vision</h3>
            <p className="mt-3 text-[#a8a8a8]">To be a trusted product engineering partner for high-growth teams worldwide.</p>
          </article>
        </div>
      </section>

      <section className="py-20">
        <div className="container-main">
          <div className="mb-8 text-center">
            <span className="section-kicker">Values</span>
            <h2 className="mt-4 text-4xl font-semibold md:text-5xl">The principles behind every project</h2>
          </div>

          <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
            {values.map((value) => (
              <article key={value.title} className="neo-card neo-card-hover p-6">
                <h3 className="text-2xl font-semibold text-white">{value.title}</h3>
                <p className="mt-3 text-sm leading-relaxed text-[#a3a3a3]">{value.description}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="py-20">
        <div className="container-main">
          <div className="neo-card p-8 md:p-10">
            <div className="mb-8 text-center">
              <span className="section-kicker">Achievements</span>
              <h2 className="mt-4 text-4xl font-semibold md:text-5xl">Measured outcomes over time</h2>
            </div>
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
              {stats.map((item) => (
                <article key={item.label} className="rounded-2xl border border-white/10 bg-[#121212] p-5 text-center">
                  <p className="text-4xl font-bold text-[rgb(249,193,37)]">{item.value}</p>
                  <p className="mt-1 text-sm font-semibold text-white">{item.label}</p>
                  <p className="mt-2 text-xs text-[#8f8f8f]">{item.detail}</p>
                </article>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="py-20">
        <div className="container-main grid gap-8 lg:grid-cols-[1.15fr_0.85fr]">
          <div className="space-y-6">
            <h2 className="text-4xl font-bold leading-tight md:text-6xl">
              Driving <span className="accent-text">Business Success</span> Through Innovation
            </h2>
            <p className="text-lg leading-relaxed text-[#adadad]">
              MindVista is a dynamic IT services company dedicated to transforming businesses through innovative digital solutions. We specialize in enhancing business operations by leveraging cutting-edge technologies and creative strategies.
            </p>
            <p className="text-lg leading-relaxed text-[#adadad]">
              Our team of experts is committed to delivering high-quality services that drive efficiency and success for our clients. With more than 100 satisfied clients, we take pride in our ability to consistently meet and exceed expectations.
            </p>
            <p className="text-lg leading-relaxed text-[#adadad]">
              Our holistic approach ensures that each solution is tailored to meet the unique needs of our clients, helping them navigate the ever-evolving digital landscape with confidence and agility.
            </p>
          </div>

          <div className="space-y-3">
            <div className="neo-card rounded-2xl p-4">
              <p className="text-sm font-semibold text-white">More than 2000 business trust us.</p>
            </div>
            <div className="neo-card rounded-2xl p-4">
              <p className="text-xs text-[#9a9a9a]">Satisfied Rate</p>
              <p className="text-3xl font-bold text-[rgb(249,193,37)]">99%</p>
            </div>
            <div className="neo-card rounded-2xl p-4">
              <p className="text-xs text-[#9a9a9a]">Successful Projects</p>
              <p className="text-3xl font-bold text-[rgb(249,193,37)]">99%</p>
            </div>
            <div className="neo-card rounded-2xl p-4">
              <p className="text-xs text-[#9a9a9a]">Served Clients</p>
              <p className="text-3xl font-bold text-white">58k</p>
            </div>
          </div>
        </div>
      </section>

      <section className="py-20">
        <div className="container-main">
          <div className="neo-card p-8 md:p-10">
            <div className="mb-8 text-center">
              <span className="section-kicker">Code of Conduct</span>
              <h2 className="mt-4 text-4xl font-semibold md:text-5xl">How We Work at MindVista</h2>
            </div>
            <div className="grid gap-4 md:grid-cols-3">
              <article className="rounded-2xl border border-white/10 bg-[#121212] p-6">
                <h3 className="text-xl font-semibold text-white">Transparent Communication</h3>
                <p className="mt-2 text-sm text-[#9f9f9f]">Clear updates, honest expectations, and visible progress throughout delivery.</p>
              </article>
              <article className="rounded-2xl border border-white/10 bg-[#121212] p-6">
                <h3 className="text-xl font-semibold text-white">Equal Treatment</h3>
                <p className="mt-2 text-sm text-[#9f9f9f]">Respectful collaboration with every stakeholder, team member, and customer.</p>
              </article>
              <article className="rounded-2xl border border-white/10 bg-[#121212] p-6">
                <h3 className="text-xl font-semibold text-white">Responsibility for Outcomes</h3>
                <p className="mt-2 text-sm text-[#9f9f9f]">We take ownership of commitments and continuously improve delivery quality.</p>
              </article>
            </div>
          </div>
        </div>
      </section>

      <section className="py-20">
        <div className="container-main text-center">
          <div className="neo-card p-8 md:p-12">
            <h2 className="text-4xl font-semibold md:text-5xl">Ready to transform your business?</h2>
            <p className="mx-auto mt-4 max-w-3xl text-lg text-[#a9a9a9]">
              Reach out to elevate your roadmap with product engineering teams that move fast and deliver reliably.
            </p>
            <div className="mt-8 flex flex-wrap justify-center gap-3">
              <Link to="/contact" className="btn-primary">Contact Us</Link>
              <Link to="/services" className="btn-outline">Explore Services</Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  )
}

export default About


